import { Badge } from '@ui';
import { useEffect, useState } from 'react';
import { CHECK_LABELS, type CheckKey } from '../../../shared/control';
import { STATUS_TITLES } from '../../../shared/board';
import { bugApi, type ReportControlView } from '../bugApi';
import { formatDateTime } from '../labels';

// Кн10 (вид — 👁): «Контроль агента» in a report's card — the stage, what moving on needs with «приложено / нет», the last refusals.
// The same checklist the gate enforces (shared/control.ts); a short list, no prose.

const labelOf = (key: string): string => CHECK_LABELS[key as CheckKey] ?? key;

/** `refresh` changes whenever the report does (status, notes): the block asks again. */
export function AgentControlBlock({ reportId, refresh }: { reportId: number; refresh: string }) {
  const [view, setView] = useState<ReportControlView | null>(null);

  useEffect(() => {
    let live = true;
    bugApi
      .reportControl(reportId)
      .then((res) => live && setView(res.control))
      .catch(() => live && setView(null));
    return () => {
      live = false;
    };
  }, [reportId, refresh]);

  if (!view) return null;
  const { steps, refusals, hard } = view;
  const next = steps.next;
  const lacking = next ? next.checks.filter((c) => !c.done && !c.withMove).length : 0;

  return (
    <div className="agent-control" data-testid="agent-control">
      <div className="board-lane-title">
        ◈ Контроль агента{' '}
        <Badge tone={hard ? 'success' : 'info'}>{hard ? 'шлюзы жёстко' : 'подсказки'}</Badge>
      </div>
      {view.taken && (
        <div className="agent-control-taken" data-testid="agent-control-taken">
          ▸ Взято: <span data-pii="">{view.taken.by ?? '—'}</span>, {formatDateTime(view.taken.at)}
        </div>
      )}
      <div className="agent-control-stage">
        <Badge tone="accent">{steps.stageTitle}</Badge>
        {next && (
          <>
            <span aria-hidden="true">→</span>
            <span>{next.title}</span>
            <Badge tone={lacking ? 'warning' : 'success'}>{lacking ? `не хватает: ${lacking}` : 'всё есть'}</Badge>
          </>
        )}
      </div>
      <ul className="agent-control-todo">
        {steps.doNow.map((line) => (
          <li key={line}>▸ {line}</li>
        ))}
      </ul>
      {next && next.checks.length > 0 && (
        <div className="agent-control-checks" aria-label={`Что нужно для «${next.title}»`}>
          {next.checks.map((c) => (
            <div key={c.key} className={`agent-control-check${c.done ? ' is-done' : ''}`} title={c.example}>
              <span aria-hidden="true">{c.done ? '✓' : c.withMove ? '↳' : '○'}</span>
              <span>{labelOf(c.key)}</span>
              <span className="jui-muted">{c.done ? 'приложено' : c.withMove ? 'с переходом' : 'нет'}</span>
            </div>
          ))}
        </div>
      )}
      {refusals.length > 0 && (
        <div className="agent-control-refusals">
          <div className="jui-muted">✕ Последние отказы</div>
          {refusals.map((r) => (
            <div key={r.at} className="agent-control-refusal" title={r.message}>
              <span>{STATUS_TITLES[r.to] ?? r.to}</span>
              <span className="jui-muted">{r.missing.map(labelOf).join(', ')}</span>
              <span className="jui-muted" data-pii="">
                {formatDateTime(r.at)} · {r.author ?? '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
